import React, { useState } from 'react';
import { Modal, ModalContent, ModalBody, Input, Button } from '@nextui-org/react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

interface DatePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onDateSelect: (date: string) => void;
  currentDate: string;
}

const DatePicker: React.FC<DatePickerProps> = ({ isOpen, onClose, onDateSelect, currentDate }) => {
  const [selectedDate, setSelectedDate] = useState(new Date(currentDate || new Date()));
  const [currentMonth, setCurrentMonth] = useState(selectedDate.getMonth());
  const [currentYear, setCurrentYear] = useState(selectedDate.getFullYear());
  const [selectedTime, setSelectedTime] = useState('12:00');

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day: number) => {
    const newDate = new Date(currentYear, currentMonth, day);
    setSelectedDate(newDate);
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTime(event.target.value);
  };

  const handleSave = () => {
    const [hours, minutes] = selectedTime.split(':');
    const newDate = new Date(selectedDate);
    newDate.setHours(parseInt(hours), parseInt(minutes));
    onDateSelect(newDate.toISOString());
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="xs"
      classNames={{
        base: "bg-white rounded-lg shadow-lg",
        header: "border-b border-gray-200",
        body: "p-0",
        closeButton: "hidden"
      }}
    >
      <ModalContent>
        <ModalBody>
          <div className="w-full bg-white rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
              <button onClick={handlePrevMonth} className="text-gray-600 hover:text-gray-800">
                <FaChevronLeft />
              </button>
              <h2 className="text-lg font-semibold text-gray-800">{months[currentMonth]} {currentYear}</h2>
              <button onClick={handleNextMonth} className="text-gray-600 hover:text-gray-800">
                <FaChevronRight />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 p-2">
              {['D', 'L', 'M', 'X', 'J', 'V', 'S'].map((day) => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                  {day}
                </div>
              ))}
              {Array.from({ length: firstDayOfMonth }, (_, i) => (
                <div key={`empty-${i}`} className="text-center py-1"></div>
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const isSelected = selectedDate.getDate() === day && 
                                   selectedDate.getMonth() === currentMonth && 
                                   selectedDate.getFullYear() === currentYear;
                const isToday = new Date().getDate() === day &&
                                new Date().getMonth() === currentMonth &&
                                new Date().getFullYear() === currentYear;
                return (
                  <button
                    key={day}
                    onClick={() => handleDateClick(day)}
                    className={`text-center py-1 rounded-full w-8 h-8 mx-auto text-sm
                      ${isSelected 
                        ? 'bg-blue-500 text-white' 
                        : isToday
                          ? 'bg-blue-100 text-blue-600'
                          : 'hover:bg-gray-200 text-gray-700'}`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <div className="p-4">
              <Input
                type="time"
                label="Hora"
                value={selectedTime}
                onChange={handleTimeChange}
                className="mb-4"
              />
              <Button color="primary" onPress={handleSave} className="w-full">
                Guardar
              </Button>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DatePicker;